import React, { useEffect, useMemo, useRef } from 'react';
import HTMLReactParser from 'html-react-parser';

import MenorahHero from '../MenorahHero';
import MenorahNav from '../MenorahNav';
import MenorahForm from '../MenorahForm';
import MenorahFaq from '../MenorahFaq';
import MenorahGallery from '../MenorahGallery';
import MenorahStickyEnquire from '../MenorahStickyEnquire';
import { useSiteQuery } from '../../../hooks';
import ErrorPage from '../../../pages/_error';

import query from './index.graphql';
import styles from './styles.module.scss';

interface Props {
  slug: string;
}

// Table-field rows -> trimmed, de-duplicated labels. Empty/null tables resolve to undefined so
// MenorahForm falls back to its built-in option lists.
const toOptionLabels = (rows?: Array<{ label?: string | null } | null> | null): string[] | undefined => {
  const labels = (rows || [])
    .map((row) => row?.label?.trim() || '')
    .filter((label, i, all) => label.length > 0 && all.indexOf(label) === i);
  return labels.length > 0 ? labels : undefined;
};

const MenorahLandingPage = ({ slug }: Props) => {
  const [{ data, fetching, error }] = useSiteQuery<LandingPageQuery>({ query, variables: { slug } });

  if (error) {
    console.warn('Landing page GraphQL query error:', error.message);
  }

  const entry = data?.entry;

  const locationName = entry?.locationName || entry?.title || null;
  const connectionOptions = toOptionLabels(entry?.formConnectionOptions);
  const interestOptions = toOptionLabels(entry?.formInterestOptions);
  const stayInvolvedOptions = toOptionLabels(entry?.formStayInvolvedOptions);
  const heroTitle = entry?.heroTitle || null;
  const heroSubtitle = entry?.heroSubtitle || null;
  const heroAddress = entry?.heroAddress || null;
  const heroIntro = entry?.heroIntro || null;
  const heroImageAsset = entry?.heroImage?.[0] || null;
  const heroImage = heroImageAsset?.url ? { url: heroImageAsset.url, title: heroImageAsset.title } : null;
  const aboutContent = entry?.aboutContent || null;
  const engagementContent = entry?.engagementContent || null;
  const locationDescription = entry?.locationDescription || null;
  const locationMapLeft = entry?.locationMapImageLeft?.[0] || null;
  const locationMapRight = entry?.locationMapImageRight?.[0] || null;
  const googleMapsUrl = entry?.googleMapsUrl || null;
  const faqCategories = (entry?.faqCategories || [])
    .filter((category): category is NonNullable<typeof category> => category !== null)
    .map((category) => ({
      categoryTitle: category.categoryTitle,
      questions: (category.questions || [])
        .filter((question): question is NonNullable<typeof question> => question !== null)
        .map((question) => ({ question: question.question || '', answer: question.answer || '' })),
    }));
  const gallery = (entry?.gallery || []).filter((p): p is AssetFragment => p !== null);
  const formHandle = entry?.form || null;
  const privacyEmail = entry?.privacyEmail || null;

  const hasForm = !!formHandle;
  const hasHero = !!(heroTitle || heroSubtitle || heroAddress || heroIntro);
  const hasAbout = !!aboutContent;
  const hasEngagement = !!engagementContent;
  const hasLocation = !!(locationDescription || locationMapLeft || locationMapRight || googleMapsUrl);
  const hasFaqs = faqCategories.length > 0;
  const hasGallery = gallery.length > 0;

  const navItems = useMemo(() => {
    const items: Array<{ id: string; label: string }> = [];
    if (hasAbout) items.push({ id: 'about', label: 'About' });
    if (hasLocation) items.push({ id: 'location', label: 'Location' });
    if (hasEngagement) items.push({ id: 'engagement', label: 'Engagement' });
    if (hasFaqs) items.push({ id: 'faqs', label: 'FAQs' });
    if (hasGallery) items.push({ id: 'gallery', label: 'Gallery' });
    return items;
  }, [hasAbout, hasEngagement, hasLocation, hasFaqs, hasGallery]);

  // Measure hero wrapper height and sync to formColumn so the orange
  // gradient background cuts off at exactly the right point
  const heroWrapperRef = useRef<HTMLDivElement>(null);
  const formColumnRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const sync = () => {
      if (!heroWrapperRef.current || !formColumnRef.current) return;
      const h = heroWrapperRef.current.offsetHeight;
      formColumnRef.current.style.setProperty('--hero-height', `${h}px`);
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [data]); // re-run when CMS data loads and hero renders

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  if (!entry) {
    if (fetching) {
      return null;
    }
    return <ErrorPage statusCode={404} />;
  }

  return (
    <>
      {/* Two-column page wrapper: left = all content, right = sticky form */}
      <div className={styles.pageWrapper}>

        {/* LEFT COLUMN */}
        <div className={styles.pageMain}>

          {/* Hero + mobile form wrapped together so we can measure their combined height */}
          <div ref={heroWrapperRef}>
            {hasHero && (
              <MenorahHero
                title={heroTitle}
                subtitle={heroSubtitle}
                address={heroAddress}
                intro={heroIntro}
                image={heroImage}
              />
            )}

            {/* Mobile-only form — sits below hero intro */}
            {hasForm && (
              <div id="register" className={styles.mobileForm}>
                <MenorahForm
                  formHandle={formHandle}
                  privacyEmail={privacyEmail}
                  locationName={locationName}
                  connectionOptions={connectionOptions}
                  interestOptions={interestOptions}
                  stayInvolvedOptions={stayInvolvedOptions}
                />
              </div>
            )}
          </div>

          {/* Section nav */}
          {navItems.length > 0 && <MenorahNav items={navItems} />}

          {/* Main content */}
          <div className={styles.content}>

            {/* About */}
            {hasAbout && aboutContent && (
              <section id="about" className={styles.section}>
                <div className={styles.richContent}>
                  {HTMLReactParser(aboutContent)}
                </div>
              </section>
            )}

            {/* Location */}
            {hasLocation && (
              <section id="location" className={styles.section}>
                <h2 className={styles.sectionTitle}>Location</h2>
                {locationDescription && (
                  <div className={styles.richContent}>
                    {HTMLReactParser(locationDescription)}
                  </div>
                )}
                {(locationMapLeft || locationMapRight) && (
                  <div className={styles.mapGrid}>
                    {locationMapLeft && (
                      <div className={styles.mapWrapper}>
                        <img
                          src={locationMapLeft.url || undefined}
                          alt={locationMapLeft.title || 'Location map overview'}
                          className={styles.locationMap}
                        />
                      </div>
                    )}
                    {locationMapRight && (
                      <div className={styles.mapWrapper}>
                        <img
                          src={locationMapRight.url || undefined}
                          alt={locationMapRight.title || 'Location map detail'}
                          className={styles.locationMap}
                        />
                      </div>
                    )}
                  </div>
                )}
                {googleMapsUrl && (
                  <p className={styles.mapLink}>
                    <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                      View on Google Maps →
                    </a>
                  </p>
                )}
              </section>
            )}

            {/* Engagement */}
            {hasEngagement && engagementContent && (
              <section id="engagement" className={styles.section}>
                <div className={styles.richContent}>
                  {HTMLReactParser(engagementContent)}
                </div>
              </section>
            )}

            {/* FAQs */}
            {hasFaqs && (
              <section id="faqs" className={styles.section}>
                <h2 className={styles.sectionTitle}>Project Information &amp; FAQs</h2>
                <MenorahFaq categories={faqCategories} />
              </section>
            )}

            {/* Gallery */}
            {hasGallery && (
              <section id="gallery" className={styles.section}>
                <h2 className={styles.sectionTitle}>Gallery</h2>
                <MenorahGallery photos={gallery} />
              </section>
            )}

          </div>
        </div>

        {/* RIGHT COLUMN — sticky form, desktop only */}
        {hasForm && (
          <aside ref={formColumnRef} className={styles.formColumn}>
            <div className={styles.formSticky}>
              <MenorahForm
                formHandle={formHandle}
                privacyEmail={privacyEmail}
                locationName={locationName}
                connectionOptions={connectionOptions}
                interestOptions={interestOptions}
                stayInvolvedOptions={stayInvolvedOptions}
              />
            </div>
          </aside>
        )}

      </div>

      {hasForm && <MenorahStickyEnquire />}
    </>
  );
};

export default MenorahLandingPage;
